import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const B = '#0B3D6E', T = '#0F6E56';

function Logo({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="17" fill="none" stroke={B} strokeWidth="1.5" strokeDasharray="76 28"/>
      <rect x="14" y="17" width="12" height="6" rx="1.5" fill={B}/>
      <rect x="17" y="14" width="6" height="12" rx="1.5" fill={T}/>
    </svg>
  );
}

export default function Navbar({ lang, setLang }) {
  const { totalItems } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const fr = lang === 'fr';
  const links = [
    { to: '/',              label: fr ? 'Accueil' : 'Home' },
    { to: '/catalogue',     label: fr ? 'Catalogue' : 'Catalogue' },
    { to: '/reservation',   label: fr ? 'Réservation' : 'Reservation' },
    { to: '/mes-commandes', label: fr ? 'Mes commandes' : 'My orders' },
    { to: '/about',         label: fr ? 'À propos' : 'About' },
    { to: '/contact',       label: 'Contact' },
  ];

  const isActive = to => location.pathname === to;

  const navStyle = {
    position: 'sticky', top: 0, zIndex: 200,
    background: '#fff',
    borderBottom: scrolled ? '0.5px solid #e2e8f0' : '0.5px solid transparent',
    boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.07)' : 'none',
    transition: 'box-shadow 0.2s, border-color 0.2s',
  };

  return (
    <>
      <nav style={navStyle}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.25rem', height: 60, display: 'flex', alignItems: 'center', gap: 8 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0, marginRight: 8 }}>
            <Logo size={32} />
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: B, letterSpacing: '-0.2px', fontFamily: "'Syne', sans-serif" }}>LOMED MEDICARE</div>
              <div style={{ fontSize: 9, color: T, letterSpacing: '0.8px', textTransform: 'uppercase' }}>{fr ? 'Distribution médicale' : 'Medical Distribution'}</div>
            </div>
          </Link>

          {/* Desktop nav links */}
          <ul style={{ display: 'flex', gap: 2, listStyle: 'none', flex: 1 }} className="desktop-nav">
            {links.map(l => (
              <li key={l.to}>
                <Link to={l.to} style={{
                  fontSize: 12.5, color: isActive(l.to) ? B : '#64748b',
                  textDecoration: 'none', padding: '5px 10px', borderRadius: 7,
                  background: isActive(l.to) ? '#EBF3FB' : 'transparent',
                  fontWeight: isActive(l.to) ? 600 : 400,
                  display: 'block', whiteSpace: 'nowrap',
                  transition: 'background 0.15s, color 0.15s',
                }}
                  onMouseEnter={e => { if (!isActive(l.to)) { e.target.style.background = '#f8fafc'; e.target.style.color = B; }}}
                  onMouseLeave={e => { if (!isActive(l.to)) { e.target.style.background = 'transparent'; e.target.style.color = '#64748b'; }}}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Right actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>

            {/* Lang switcher */}
            <div style={{ display: 'flex', border: '0.5px solid #e2e8f0', borderRadius: 7, overflow: 'hidden' }} className="desktop-nav">
              {['fr', 'en'].map(l => (
                <button key={l} onClick={() => setLang(l)} style={{
                  background: lang === l ? B : '#fff', color: lang === l ? '#fff' : '#94a3b8',
                  border: 'none', padding: '4px 10px', fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', transition: 'background 0.15s',
                }}>{l.toUpperCase()}</button>
              ))}
            </div>

            {/* StockMed button */}
            <Link to="/stock" className="desktop-nav" style={{
              background: T, color: '#fff', padding: '6px 12px', borderRadius: 7,
              fontSize: 11, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'none', stroke: '#fff', strokeWidth: 2 }}>
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
              </svg>
              StockMed
            </Link>

            {/* Devis CTA */}
            <Link to="/devis" className="desktop-nav" style={{
              background: B, color: '#fff', padding: '7px 14px', borderRadius: 7,
              fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
              border: `1.5px solid ${B}`,
            }}>
              {fr ? 'Demander un devis' : 'Request a quote'}
            </Link>

            {/* Cart */}
            <Link to="/reservation" style={{
              position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, background: '#f1f5f9', border: '0.5px solid #e2e8f0',
              borderRadius: 8, textDecoration: 'none',
            }}>
              <svg viewBox="0 0 24 24" style={{ width: 15, height: 15, fill: 'none', stroke: '#475569', strokeWidth: 2 }}>
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              {totalItems > 0 && (
                <span style={{
                  position: 'absolute', top: -5, right: -5,
                  background: '#E24B4A', color: '#fff', fontSize: 9, fontWeight: 700,
                  width: 16, height: 16, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{totalItems}</span>
              )}
            </Link>

            {/* Admin */}
            <button onClick={() => navigate('/admin/login')} className="desktop-nav" style={{
              background: '#1a2535', color: '#fff', border: 'none',
              padding: '6px 11px', borderRadius: 7, fontSize: 11, fontWeight: 500, cursor: 'pointer',
            }}>Admin</button>

            {/* Mobile hamburger */}
            <button onClick={() => setMenuOpen(!menuOpen)} className="mobile-nav" style={{
              background: 'none', border: '0.5px solid #e2e8f0', borderRadius: 7,
              padding: '7px 9px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <span style={{ display: 'block', width: 18, height: 1.5, background: menuOpen ? '#0B3D6E' : '#475569', borderRadius: 2, transform: menuOpen ? 'translateY(5.5px) rotate(45deg)' : 'none', transition: 'transform 0.2s' }}/>
              <span style={{ display: 'block', width: 18, height: 1.5, background: '#475569', borderRadius: 2, opacity: menuOpen ? 0 : 1, transition: 'opacity 0.2s' }}/>
              <span style={{ display: 'block', width: 18, height: 1.5, background: menuOpen ? '#0B3D6E' : '#475569', borderRadius: 2, transform: menuOpen ? 'translateY(-5.5px) rotate(-45deg)' : 'none', transition: 'transform 0.2s' }}/>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 199, background: 'rgba(0,0,0,0.45)' }} onClick={() => setMenuOpen(false)}>
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 280,
            background: '#fff', padding: '1.25rem',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
            animation: 'fadeIn 0.2s ease',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Logo size={26} />
                <span style={{ fontSize: 12, fontWeight: 600, color: B, fontFamily: "'Syne', sans-serif" }}>LOMED MEDICARE</span>
              </div>
              <button onClick={() => setMenuOpen(false)} style={{ background: '#f1f5f9', border: 'none', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', fontSize: 18, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            {links.map(l => (
              <Link key={l.to} to={l.to} style={{
                display: 'block', padding: '11px 12px', marginBottom: 3, borderRadius: 9,
                textDecoration: 'none', fontSize: 13, fontWeight: isActive(l.to) ? 600 : 400,
                color: isActive(l.to) ? B : '#475569',
                background: isActive(l.to) ? '#EBF3FB' : 'transparent',
              }}>{l.label}</Link>
            ))}

            <div style={{ borderTop: '0.5px solid #e2e8f0', marginTop: '1rem', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Link to="/devis" style={{ background: B, color: '#fff', padding: '11px', borderRadius: 9, fontSize: 13, fontWeight: 600, textDecoration: 'none', textAlign: 'center' }}>
                {fr ? '📋 Demander un devis' : '📋 Request a quote'}
              </Link>
              <Link to="/stock" style={{ background: T, color: '#fff', padding: '10px', borderRadius: 9, fontSize: 13, fontWeight: 600, textDecoration: 'none', textAlign: 'center' }}>StockMed</Link>
              <button onClick={() => { navigate('/admin/login'); }} style={{ background: '#1a2535', color: '#fff', border: 'none', padding: '10px', borderRadius: 9, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Admin</button>
            </div>

            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
              {['fr', 'en'].map(l => (
                <button key={l} onClick={() => setLang(l)} style={{
                  flex: 1, background: lang === l ? B : '#f1f5f9', color: lang === l ? '#fff' : '#64748b',
                  border: 'none', padding: '8px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

